import { useState } from 'react';
import { X, Upload } from 'lucide-react';
import { useEntrance } from '../context/entrance-context';

interface SeedImportModalProps {
  onClose: () => void;
}

export function SeedImportModal({ onClose }: SeedImportModalProps) {
  const { importSeed } = useEntrance();
  const [json, setJson] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleImport = () => {
    try {
      const parsed = JSON.parse(json);
      if (typeof parsed !== 'object' || parsed === null) {
        setError('JSON must be an object with entrance mappings');
        return;
      }
      importSeed(parsed);
      onClose();
    } catch {
      setError('Invalid JSON. Paste the entrance section of your seed/spoiler log.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl w-full max-w-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Upload size={16} />
            Import Seed JSON
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 flex flex-col gap-3">
          <p className="text-xs text-gray-400">
            Paste the entrance section of your seed or spoiler log JSON. Supports both simple
            string format and decoupled <code className="text-gray-300">{`{region, from}`}</code> format.
          </p>
          <textarea
            className="w-full h-48 bg-gray-800 text-white text-xs font-mono px-3 py-2 rounded border border-gray-600 outline-none focus:border-zelda-gold resize-none"
            placeholder='{"Death Mountain -> Goron City": "Temple of Time", ...}'
            value={json}
            onChange={(e) => {
              setJson(e.target.value);
              setError(null);
            }}
          />
          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!json.trim()}
            className="px-3 py-1.5 text-xs font-medium bg-zelda-gold/20 text-zelda-gold border border-zelda-gold/30 rounded hover:bg-zelda-gold/30 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Import
          </button>
        </div>
      </div>
    </div>
  );
}
